'use client';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Suspense } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/src/hooks/useAuth';

// Zod schema for login form validation
const loginFormSchema = z.object({
  email: z.email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginFormSchema>;

function LoginForm() {
  const sp = useSearchParams();
  const callbackUrl = sp.get('callbackUrl') || '/en/dashboards';
  const { login } = useAuth();
  const t = useTranslations('auth.login');
  const tCommon = useTranslations('common');

  // Initialize form with react-hook-form + Zod validation
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onChange',
  });

  // Form submission handler
  async function onSubmit(data: LoginFormData) {
    try {
      await login.mutateAsync({
        email: data.email,
        password: data.password,
        callbackUrl,
      });
    } catch (error) {
      // Error handling is now managed by React Query
      console.error('Login error:', error);
    }
  }

  // Calculate loading state
  const isLoading = isSubmitting || login.isPending;

  return (
    <div className="grid min-h-screen place-items-center p-6">
      <Card className="w-full max-w-sm space-y-4 p-6">
        <h1 className="text-xl font-semibold">{t('title')}</h1>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <div>
            <Label htmlFor="email">{t('email')}</Label>
            <Input
              id="email"
              type="email"
              {...register('email')}
              disabled={isLoading}
            />
            {errors.email && (
              <p className="text-destructive text-sm">{errors.email.message}</p>
            )}
          </div>
          <div>
            <Label htmlFor="password">{t('password')}</Label>
            <Input
              id="password"
              type="password"
              {...register('password')}
              disabled={isLoading}
            />
            {errors.password && (
              <p className="text-destructive text-sm">
                {errors.password.message}
              </p>
            )}
          </div>
          {login.error && (
            <p className="text-sm text-red-600">
              {login.error.message === 'CredentialsSignin'
                ? t('invalidCredentials')
                : login.error.message}
            </p>
          )}
          <Button type="submit" className="w-full" disabled={isLoading}>
            {login.isPending ? t('signingIn') : tCommon('signIn')}
          </Button>
        </form>
        <p className="text-center text-sm">
          {t('newHere')}{' '}
          <Link className="underline" href="/en/register">
            {t('createAccount')}
          </Link>
        </p>
        <p className="text-center text-xs text-gray-500">
          {t('afterRegistration')}
        </p>
      </Card>
    </div>
  );
}

export default function LoginPage() {
  const t = useTranslations('auth.login');
  const tCommon = useTranslations('common');

  return (
    <Suspense
      fallback={
        <div className="grid min-h-screen place-items-center p-6">
          <Card className="w-full max-w-sm space-y-4 p-6">
            <h1 className="text-xl font-semibold">{t('title')}</h1>
            <div className="text-center">{tCommon('loading')}</div>
          </Card>
        </div>
      }
    >
      <LoginForm />
    </Suspense>
  );
}
